import { makeAutoObservable, observable, runInAction } from "mobx";
import { RouteStatus } from "@/types/route/route.common";
import { createOrderFingerprint, createRouteFingerprint } from "@/utils/dataFingerprint";
export class DashboardStore {
    constructor() {
        // === Selection State ===
        this.selectedOrderId = null;
        this.selectedRouteId = null;
        this.selectionType = null;
        // === Route Filters ===
        this.dateFilter = 'today';
        this.routeStatusFilters = new Set([
            RouteStatus.PLANNED,
            RouteStatus.IN_PROGRESS,
            RouteStatus.NOT_ACCEPTED,
        ]);
        // === Map Layers ===
        this.mapLayerVisibility = {
            technicians: false,
            bases: false,
        };
        // === Smart Update: Orders ===
        this.ordersMap = observable.map();
        this.orderFingerprints = new Map();
        this.updatedOrderIds = observable.set();
        this.ordersLoading = false;
        this.ordersError = null;
        // === Smart Update: Routes ===
        this.routesMap = observable.map();
        this.routeFingerprints = new Map();
        this.updatedRouteIds = observable.set();
        this.routesLoading = false;
        this.routesError = null;
        makeAutoObservable(this);
    }
    // === Selection Actions ===
    selectOrder(orderId) {
        runInAction(() => {
            this.selectedOrderId = orderId;
            this.selectedRouteId = null;
            this.selectionType = 'order';
        });
    }
    selectRoute(routeId) {
        runInAction(() => {
            this.selectedRouteId = routeId;
            this.selectedOrderId = null;
            this.selectionType = 'route';
        });
    }
    clearSelection() {
        runInAction(() => {
            this.selectedOrderId = null;
            this.selectedRouteId = null;
            this.selectionType = null;
        });
    }
    // === Route Filter Actions ===
    setDateFilter(filter) {
        runInAction(() => {
            this.dateFilter = filter;
        });
    }
    toggleRouteStatusFilter(status) {
        runInAction(() => {
            if (this.routeStatusFilters.has(status)) {
                // Не позволять отключить все статусы
                if (this.routeStatusFilters.size > 1) {
                    this.routeStatusFilters.delete(status);
                }
            }
            else {
                this.routeStatusFilters.add(status);
            }
        });
    }
    isStatusFilterActive(status) {
        return this.routeStatusFilters.has(status);
    }
    // === Map Layer Actions ===
    toggleMapLayer(layer) {
        runInAction(() => {
            this.mapLayerVisibility[layer] = !this.mapLayerVisibility[layer];
        });
    }
    setMapLayerVisibility(layer, visible) {
        runInAction(() => {
            this.mapLayerVisibility[layer] = visible;
        });
    }
    // === Smart Update: Orders ===
    setOrdersLoading(loading) {
        runInAction(() => {
            this.ordersLoading = loading;
        });
    }
    setOrdersError(error) {
        runInAction(() => {
            this.ordersError = error;
        });
    }
    /**
     * Умное обновление списка заказов
     * - Сравнивает fingerprints для определения изменений
     * - Обновляет только изменившиеся записи в Map
     * - Помечает изменённые записи бейджем "обновлено"
     */
    smartUpdateOrders(newOrders) {
        runInAction(() => {
            const newIds = new Set(newOrders.map((o) => o.id));
            // 1. Очистить бейджи предыдущего цикла
            this.updatedOrderIds.clear();
            // 2. Удалить отсутствующие записи
            for (const id of this.ordersMap.keys()) {
                if (!newIds.has(id)) {
                    this.ordersMap.delete(id);
                    this.orderFingerprints.delete(id);
                }
            }
            // 3. Добавить/обновить записи
            for (const order of newOrders) {
                const newFingerprint = createOrderFingerprint(order);
                const oldFingerprint = this.orderFingerprints.get(order.id);
                if (oldFingerprint !== newFingerprint) {
                    // Данные изменились
                    this.ordersMap.set(order.id, order);
                    this.orderFingerprints.set(order.id, newFingerprint);
                    // Бейдж только если это не первая загрузка
                    if (oldFingerprint !== undefined) {
                        this.updatedOrderIds.add(order.id);
                    }
                }
                // Если fingerprint совпадает — не трогаем Map (нет перерисовки)
            }
        });
    }
    /**
     * Проверка наличия бейджа "обновлено" для заказа
     */
    isOrderUpdated(orderId) {
        return this.updatedOrderIds.has(orderId);
    }
    // === Smart Update: Routes ===
    setRoutesLoading(loading) {
        runInAction(() => {
            this.routesLoading = loading;
        });
    }
    setRoutesError(error) {
        runInAction(() => {
            this.routesError = error;
        });
    }
    /**
     * Умное обновление списка маршрутов
     * - Сравнивает fingerprints для определения изменений
     * - Обновляет только изменившиеся записи в Map
     * - Помечает изменённые записи бейджем "обновлено"
     */
    smartUpdateRoutes(newRoutes) {
        runInAction(() => {
            const newIds = new Set(newRoutes.map((r) => r.routeId));
            // 1. Очистить бейджи предыдущего цикла
            this.updatedRouteIds.clear();
            // 2. Удалить отсутствующие записи
            for (const id of this.routesMap.keys()) {
                if (!newIds.has(id)) {
                    this.routesMap.delete(id);
                    this.routeFingerprints.delete(id);
                }
            }
            // 3. Добавить/обновить записи
            for (const route of newRoutes) {
                const newFingerprint = createRouteFingerprint(route);
                const oldFingerprint = this.routeFingerprints.get(route.routeId);
                if (oldFingerprint !== newFingerprint) {
                    // Данные изменились
                    this.routesMap.set(route.routeId, route);
                    this.routeFingerprints.set(route.routeId, newFingerprint);
                    // Бейдж только если это не первая загрузка
                    if (oldFingerprint !== undefined) {
                        this.updatedRouteIds.add(route.routeId);
                    }
                }
                // Если fingerprint совпадает — не трогаем Map (нет перерисовки)
            }
        });
    }
    /**
     * Проверка наличия бейджа "обновлено" для маршрута
     */
    isRouteUpdated(routeId) {
        return this.updatedRouteIds.has(routeId);
    }
    /**
     * Полная очистка данных маршрутов (при смене фильтров)
     */
    clearRoutes() {
        runInAction(() => {
            this.routesMap.clear();
            this.routeFingerprints.clear();
            this.updatedRouteIds.clear();
        });
    }
    // === Computed ===
    get hasSelection() {
        return this.selectionType !== null;
    }
    get routeStatusFiltersArray() {
        return Array.from(this.routeStatusFilters);
    }
    get ordersArray() {
        return Array.from(this.ordersMap.values());
    }
    get routesArray() {
        return Array.from(this.routesMap.values());
    }
    get ordersCount() {
        return this.ordersMap.size;
    }
    get routesCount() {
        return this.routesMap.size;
    }
    // === Cleanup ===
    reset() {
        runInAction(() => {
            this.selectedOrderId = null;
            this.selectedRouteId = null;
            this.selectionType = null;
            this.dateFilter = 'today';
            this.routeStatusFilters = new Set([
                RouteStatus.PLANNED,
                RouteStatus.IN_PROGRESS,
                RouteStatus.NOT_ACCEPTED,
            ]);
            this.mapLayerVisibility = {
                technicians: false,
                bases: false,
            };
            // Очистка данных умного обновления
            this.ordersMap.clear();
            this.orderFingerprints.clear();
            this.updatedOrderIds.clear();
            this.ordersLoading = false;
            this.ordersError = null;
            this.routesMap.clear();
            this.routeFingerprints.clear();
            this.updatedRouteIds.clear();
            this.routesLoading = false;
            this.routesError = null;
        });
    }
}
